import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Panel } from '../../components/Panel';
import { Button } from '../../components/Button';
import { Spinner, ErrorBox } from '../../components/Spinner';
import {
  runUploadOcr,
  getUpload,
  listUploads,
  uploadFileUrl,
  saveCorrections,
  searchProducts,
  llmSearchProducts,
  productLookup,
  saveOrder,
  updateUploadState,
  deleteUpload,
  listStaff,
  listSites,
  listTodoke,
  getWarehouseByCustomer,
} from '../../api/endpoints';
import { formatJST, sjisByteLength } from '../../utils/format';
import { ocrReducer, initialOcrState, selectors, isCompleteDate, SPOT_PRODUCT_CODE, slipInfo, erpProductCodeOf, subtotalLabelOf } from './state';
import type { Row } from './state';
import type { Correction, Upload, Staff, Todoke, SearchResult } from '../../types/api';
import { PreviewPanel } from './components/PreviewPanel';
import { HeaderEditor } from './components/HeaderEditor';
import { RowTable } from './components/RowTable';
import { MatchPanel } from './components/MatchPanel';
import { BulkCodeModal } from './components/BulkCodeModal';
import { Modal } from '../../components/Modal';
import { ERP_ITEM_LIMIT, ERP_ITEM_MAX } from '../../utils/erpLimits';

// 見積でマスタにマッチしない明細を一括登録する際の仮商品コード（汎用コードと同一値）
const SPOT_ESTIMATE_CODE = SPOT_PRODUCT_CODE;

// 伝票分割（はさみボタン）の表示。VITE_DISABLE_SLIP_SPLIT=true でビルドすると隠す。
// 2026-10-03 の本番確認で、全伝票に同じ相手伝票No.を入れると2枚目以降がカクテルに
// 「既に伝票が存在しています」で拒否されたため一時的に隠していた。相手伝票No.を
// 分割した注文では相手伝票No.を送らない（画面でも入力不可にする）形にして再開する。
// 止める必要が出たときはこの設定で隠す。
// 隠している間は、一時保存に残っていた区切りも読み込まない（＝保存時にも送らない）ため、
// サーバーには1伝票として書き込まれ、分割前と同じ動きになる。
const SLIP_SPLIT_ENABLED = import.meta.env.VITE_DISABLE_SLIP_SPLIT !== 'true';
const withoutSlips = <T extends { slip_group?: number }>(items: T[]): T[] =>
  SLIP_SPLIT_ENABLED ? items : items.map((it) => ({ ...it, slip_group: undefined }));

function WarehouseCell({ customerCode, warehouseName }: { customerCode: string; warehouseName?: string }) {
  const [name, setName] = useState(warehouseName || '');
  useEffect(() => {
    if (warehouseName || !customerCode) return;
    void (async () => {
      try {
        const info = await getWarehouseByCustomer(customerCode);
        setName(info.name);
      } catch { /* silent */ }
    })();
  }, [customerCode, warehouseName]);
  return <>{name || '-'}</>;
}

export function OcrPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const uploadId = searchParams.get('uploadId');

  const [state, dispatch] = useReducer(ocrReducer, initialOcrState);
  const [error, setError] = useState<Error | null>(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [matchModalRowId, setMatchModalRowId] = useState<string | null>(null);
  // 見積のコード一括登録: チェックした明細と、一括登録画面の開閉
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [showBulkCode, setShowBulkCode] = useState(false);
  const [showErpConfirm, setShowErpConfirm] = useState(false);

  // 担当者マスタ・届け先マスタ
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [todokeList, setTodokeList] = useState<Todoke[]>([]);
  useEffect(() => {
    void (async () => {
      try { setStaffList((await listStaff()).items); } catch { /* silent */ }
      try { setTodokeList((await listTodoke()).items); } catch { /* silent */ }
    })();
  }, []);

  // アップロード一覧（uploadId 未指定時に表示）
  const [uploads, setUploads] = useState<Upload[] | null>(null);
  const [uploadsError, setUploadsError] = useState<Error | null>(null);
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;

  // 書類種別（注文/見積）。見積は EDNRWK へ書込み、UIに区分を表示する。
  // erpBlockReason 等より前で参照するためここで宣言する。
  const [docType, setDocType] = useState<'order' | 'estimate' | 'estimate_maker'>('order');
  // 見積判定は estimate/estimate_maker の両方を「見積」とみなす（価格列・EDNRWK書込み等）。
  const isEstimate = docType.startsWith('estimate');
  // メーカー見積: マスタ外スポット品。商品検索せず全明細に仮コードを自動付与する。
  const isMaker = docType === 'estimate_maker';
  // アップロード時に「商品検索をしない(OCRのみ)」が指定されていたか。自動一括検索を抑止する。
  const [skipSearch, setSkipSearch] = useState(false);

  const hasUnsaved = selectors.hasUnsavedChanges(state);
  const allConfirmed = selectors.allConfirmed(state);
  const currentDeptCode = staffList.find((s) => s.code === state.header.input_staff_code)?.department_code ?? '';
  const currentWarehouseCode = state.header.warehouse_code ?? '';
  // 相手伝票No.(ADNID)・連絡先(TELCM)はERPのカラム幅が15バイト。桁溢れするとカクテル
  // 取込でエラーになるため、OCR自動反映値も含めERP書込み前に弾く。受注のみ対象
  // (見積EDNRWKには相手伝票No.列が無く、両項目とも画面非表示)。
  // 相手伝票No.はERP側が日本語文字列に対応したため文字種の制限を外し、幅だけを見る。
  // 連絡先は従来どおり半角限定。
  const restrictedCharBlock = (() => {
    if (isEstimate) return '';
    const checkAscii = (label: string, v?: string) => {
      const s = v ?? '';
      if (/[^\x20-\x7E]/.test(s)) return `${label}に全角・日本語は使えません（半角で入力してください）`;
      if (s.length > 15) return `${label}は15文字以内で入力してください`;
      return '';
    };
    // バックエンドの切り詰め(_trunc)と同じ Shift_JIS バイト数で判定する。
    // 画面で収まっていればERP書込み時に無言で切られない。
    const checkBytes = (label: string, v: string | undefined, maxBytes: number) => {
      if (sjisByteLength(v ?? '') > maxBytes)
        return `${label}は半角${maxBytes}文字（全角${Math.floor(maxBytes / 2)}文字）以内で入力してください`;
      return '';
    };
    return checkBytes('相手伝票No.', state.header.slip_no, 15)
      || checkAscii('連絡先', state.header.contact);
  })();
  // ERP書込みが無効な「具体的な理由」を1つ返す（汎用文言だと原因が分からないため）。
  // 空文字なら書込み可能。ERP書込みボタンの disabled はこの値だけで決める（判定の一本化）。
  // disabled を別実装で持つと両者がずれた行で「非活性なのに理由が出ない」無言のブロックに
  // なる（例: 品名列が空でもマッチ済みなら商品名1が埋まるため書込み可能な明細行）。
  const erpBlockReason = (() => {
    // ⓪ 明細行数。カクテルは1伝票あたりの行数に上限があり、超えると取込時に
    //   「1000行を超えています。」で弾かれる。弾かれた行は伝票にならずワークテーブルに
    //   残るだけなので、送る前にここで止める。ヘッダー確認より先に出す（1,000行を
    //   全部直してから止められるのを避けるため）。
    //   伝票分割している場合は伝票ごとに見る（上限は1伝票あたりの行数）。
    const slipCheck = slipInfo(state.rows);
    const overIdx = slipCheck.sizes.findIndex((n) => n >= ERP_ITEM_LIMIT);
    if (overIdx >= 0) {
      const where = slipCheck.count > 1 ? `伝票 ${overIdx + 1}/${slipCheck.count} の` : '';
      return `${where}明細が ${slipCheck.sizes[overIdx].toLocaleString()} 行あります。ERPへ書き込めるのは1伝票 `
        + `${ERP_ITEM_MAX.toLocaleString()} 行までです。伝票を分けてください`;
    }
    // ① ヘッダー
    if (!state.customerCode) return '得意先を確定してください';
    if (!isEstimate && !state.siteCode) return '現場を確定してください';
    const code = state.header.input_staff_code ?? '';
    if (!code) return '入力担当者コードを入力してください';
    if (code.length !== 4) return '入力担当者コードは4桁で入力してください';
    if (staffList.length > 0 && !staffList.some((s) => s.code === code))
      return `入力担当者コード（${code}）が担当者マスタに存在しません`;
    if (!isEstimate && !state.header.todoke_code) return '届け先を選択してください';
    if (!isCompleteDate(state.header.order_date))
      return `${isEstimate ? '見積' : '受注'}日付を入力してください`;
    if (restrictedCharBlock) return restrictedCharBlock;
    // ② 明細確定
    if (!allConfirmed) return '未確定の商品があります（検索または仮コード登録で確定してください）';
    // ③ 商品名・個数（どの行が原因か分かるよう No. を添える）
    const rows = selectors.visibleRows(state);
    // 表示No.（line_no。無ければ表示順の連番）
    const noOf = (r: Row, idx: number) => r.line_no ?? idx + 1;
    // ERPへ書かれる商品名1(HINNMA)。マッチ済みならマスタ商品名、OCR名採用なら品名。
    // ※「品名(注文書)」列が空でも、マッチ済みで商品名1が埋まっていれば書込み可能。
    // ERPへ書かれる商品名1/2(HINNMA/HINNMB)。保存時に送る値（handleSaveOrder）と同じ決め方。
    const erpNames = (r: Row) => {
      const useOcr = state.ocrNameRows.has(r.id);
      const m = state.matches[r.id];
      const cand = m ? m.candidates.find((c) => c.id === m.matchedId) : undefined;
      return useOcr
        ? [r.product_name, r.size]
        : [cand?.name1 || m?.matchedName || '', cand?.name2 || ''];
    };
    // 備考(3)は商品名1が空でもよい。区切りの空白行として使う運用があり、ERP側も
    // 商品名1(HINNMA)はNULL可で空の登録実績がある（区分4で97件）。三興バルブ様確認済み。
    // 明細(0)も、商品名2があれば商品名1が空でも書き込める。書類の品名欄が空で寸法だけ
    // 読めた行に汎用コードを付けると商品名1が空になり、書き込めなかった（三興バルブ様要望 10/22）。
    const nameIdx = rows.findIndex(
      (r) => (r.row_type ?? 'detail') === 'detail' && !erpNames(r).some((n) => (n ?? '').trim()),
    );
    if (nameIdx >= 0)
      return `商品名1・商品名2がどちらも空の明細があります（No.${noOf(rows[nameIdx], nameIdx)}）。品名か寸法を入力するか、商品を検索してください`;
    const kosuIdx = rows.findIndex(
      (r) => (r.row_type ?? 'detail') === 'detail' && !r.kosu,
    );
    if (kosuIdx >= 0) return `個数が未入力の明細があります（No.${noOf(rows[kosuIdx], kosuIdx)}）`;
    return '';
  })();
  // ERPへ書き込む商品コード。判定は state.ts の erpProductCodeOf に一本化している。
  const erpProductCode = (r: Row): string => erpProductCodeOf(state, r);

  const hasMatchChanges = Object.keys(state.matches).some((rowId) => {
    const orig = state.matchesOriginal[rowId];
    const curr = state.matches[rowId];
    return orig && curr && orig.matchedId !== curr.matchedId;
  });
  const hasOcrNameChanges =
    state.ocrNameRows.size !== state.ocrNameRowsOriginal.size ||
    [...state.ocrNameRows].some((id) => !state.ocrNameRowsOriginal.has(id));

  const loadUploads = useCallback(async () => {
    try {
      const data = await listUploads();
      setUploadsError(null);
      setUploads((data.items ?? []).filter((u) => !u.order_id));
    } catch (e) {
      setUploadsError(e as Error);
      setUploads(null);
    }
  }, []);

  // uploadId 未指定時にアップロード一覧を取得 + 処理中があればポーリング
  useEffect(() => {
    if (uploadId) return;
    void loadUploads();
    const interval = setInterval(() => {
      if (uploadsRef.current?.some((u) => u.ocr_processing)) {
        void loadUploads();
      }
    }, 3000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadId, loadUploads]);

  // バックグラウンド OCR 処理中フラグ
  const [ocrProcessing, setOcrProcessing] = useState(false);
  // 複数ファイルプレビュー切り替え
  const [previewFileCount, setPreviewFileCount] = useState(1);
  const [previewFileIndex, setPreviewFileIndex] = useState(0);
  // リサイズ可能なスプリットペイン（左パネルの幅 %）
  const [splitPercent, setSplitPercent] = useState(45);
  const [isDragging, setIsDragging] = useState(false);
  // よく使う商品（セッション内で保持）
  const [recentProducts, setRecentProducts] = useState<Array<{ id: string; name1: string; name2?: string; category?: string }>>([]);
  // 保存済み検索結果（rows が確定してから適用する）
  const [pendingSearchResults, setPendingSearchResults] = useState<
    Array<{ row_index?: number; line_no: number; matched_id: string; matched_name: string; candidates: Array<{ id: string; name1?: string; name2?: string; category?: string; score?: number }>; use_ocr_name?: boolean }> | null
  >(null);

  // uploadId が指定されている場合、メタデータ取得 + プレビュー設定 + キャッシュ済み OCR 結果を適用
  useEffect(() => {
    if (!uploadId || state.previewUrl) return;
    let cancelled = false;
    void (async () => {
      try {
        const meta = await getUpload(uploadId);
        if (cancelled) return;
        setDocType(meta.doc_type || 'order');
        setSkipSearch(!!meta.skip_search);
        const filenames = meta.files.map((f) => f.filename).join(', ');
        setPreviewFileCount(meta.files.length);
        setPreviewFileIndex(0);
        dispatch({
          type: 'UPLOAD_LOADED',
          uploadId,
          fileName: filenames,
          previewUrl: uploadFileUrl(uploadId, 0),
        });
        if (meta.ocr_processing) {
          setOcrProcessing(true);
          setStatusMsg('バックグラウンド OCR 処理中...');
        } else if (meta.ocr_done && meta.ocr_header && meta.ocr_items) {
          dispatch({
            type: 'OCR_DONE',
            header: meta.ocr_header,
            sourceId: meta.ocr_source_id || '',
            items: withoutSlips(meta.ocr_items),
          });
          // ヘッダー確定状態を復元
          if (meta.header_confirmed && meta.confirmed_header) {
            dispatch({ type: 'HEADER_EDIT', field: 'customer_name', value: meta.confirmed_header.customer_name ?? '' });
            dispatch({ type: 'HEADER_EDIT', field: 'orderer_name', value: meta.confirmed_header.orderer_name ?? '' });
            dispatch({ type: 'HEADER_EDIT', field: 'order_date', value: meta.confirmed_header.order_date ?? '' });
            dispatch({ type: 'HEADER_EDIT', field: 'staff_code', value: meta.confirmed_header.staff_code ?? '' });
            if (meta.confirmed_customer_code) {
              dispatch({ type: 'CUSTOMER_SET', code: meta.confirmed_customer_code, name: meta.confirmed_header.customer_name ?? '', abbr: meta.confirmed_header.customer_abbr ?? '' });
            }
            if (meta.confirmed_site_code) {
              const siteCode = meta.confirmed_site_code!;
              const tokcd = meta.confirmed_customer_code || '';
              void (async () => {
                try {
                  const res = await listSites({ tokcd });
                  const site = res.items.find((s) => s.code === siteCode);
                  dispatch({ type: 'SITE_SET', code: siteCode, name: site?.name || site?.abbr, abbr: site?.abbr });
                } catch {
                  dispatch({ type: 'SITE_SET', code: siteCode });
                }
              })();
            }
            dispatch({ type: 'HEADER_CONFIRM' });
          }
          if (meta.search_done && meta.search_results) {
            setPendingSearchResults(meta.search_results);
            setAutoSearchDone(true);
            setStatusMsg('検索結果をロードしました');
          } else {
            setStatusMsg('OCR 結果をロードしました');
          }
        }
        // アップロード時の入力担当者コードを引き継ぎ（OCR_DONEの後に適用）
        if (meta.uploader) {
          dispatch({ type: 'HEADER_EDIT', field: 'input_staff_code', value: meta.uploader });
        }
      } catch {
        // silent
      }
    })();
    return () => { cancelled = true; };
  }, [uploadId, state.previewUrl]);

  // バックグラウンド OCR 処理中のポーリング
  useEffect(() => {
    if (!ocrProcessing || !uploadId) return;
    const interval = setInterval(async () => {
      try {
        const meta = await getUpload(uploadId);
        if (!meta.ocr_processing) {
          setOcrProcessing(false);
          if (meta.ocr_done && meta.ocr_header && meta.ocr_items) {
            dispatch({
              type: 'OCR_DONE',
              header: meta.ocr_header,
              sourceId: meta.ocr_source_id || '',
              items: withoutSlips(meta.ocr_items),
            });
            setStatusMsg('OCR 完了 — 自動検索を開始します');
          } else {
            setStatusMsg('');
          }
        }
      } catch {
        // silent
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [ocrProcessing, uploadId]);


  // 保存済み検索結果を復元（rows 確定後に適用）
  useEffect(() => {
    if (!pendingSearchResults || state.rows.length === 0) return;
    const visibleRows = selectors.visibleRows(state);
    for (const sr of pendingSearchResults) {
      const row = sr.row_index != null && sr.row_index < visibleRows.length
        ? visibleRows[sr.row_index]
        : state.rows.find((r) => r.line_no === sr.line_no);
      if (row && sr.matched_id) {
        dispatch({
          type: 'MATCH_SET',
          rowId: row.id,
          match: {
            matchedId: sr.matched_id,
            matchedName: sr.matched_name,
            candidates: sr.candidates,
          },
        });
        if (sr.use_ocr_name) {
          dispatch({ type: 'TOGGLE_OCR_NAME', rowId: row.id });
        }
      }
    }
    dispatch({ type: 'SYNC_OCR_NAME_ORIGINAL' });
    setPendingSearchResults(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSearchResults, state.rows.length]);

  // OCR 結果ロード後、商品検索が未実行なら自動で一括検索
  const [autoSearchDone, setAutoSearchDone] = useState(false);
  useEffect(() => {
    if (
      autoSearchDone ||
      skipSearch ||
      state.phase !== 'ocr_done' ||
      state.rows.length === 0 ||
      Object.keys(state.matches).length > 0
    ) return;

    setAutoSearchDone(true);
    // メーカー見積: マスタ外のスポット品のため商品検索は行わず、全明細に仮コードを自動付与する。
    if (isMaker) {
      selectors.visibleRows(state).forEach((row) => {
        if (row.row_type && row.row_type !== 'detail') return;
        dispatch({
          type: 'MATCH_SET',
          rowId: row.id,
          match: {
            matchedId: SPOT_ESTIMATE_CODE,
            matchedName: row.product_name,
            candidates: [{ id: SPOT_ESTIMATE_CODE, name1: row.product_name, name2: row.size, category: '' }],
          },
        });
      });
      setStatusMsg(`メーカー見積: 全明細に仮コード(${SPOT_ESTIMATE_CODE})を付与しました`);
      return;
    }
    void (async () => {
      const visibleRows = selectors.visibleRows(state);
      setStatusMsg(`自動検索中... 0/${visibleRows.length}`);
      let done = 0;
      const searchResultsToSave: Array<{ row_index: number; line_no: number; matched_id: string; matched_name: string; candidates: unknown[] }> = [];

      for (let idx = 0; idx < visibleRows.length; idx++) {
        const row = visibleRows[idx];
        // 備考(区分3)・小計(区分4)は商品がないので検索しない
        if (row.row_type && row.row_type !== 'detail') { done++; continue; }
        const query = `${row.product_name ?? ''} ${row.size ?? ''}`.trim();
        if (!query) { done++; continue; }
        try {
          const data = await searchProducts({ query, top_k: 10, normalize: true, dept_code: currentDeptCode, warehouse_code: currentWarehouseCode });
          const candidates = data.results ?? [];
          const top = candidates[0];
          if (top) {
            dispatch({
              type: 'MATCH_SET',
              rowId: row.id,
              match: {
                matchedId: top.id,
                matchedName: top.name1 || top.name2 || '',
                candidates,
              },
            });
            searchResultsToSave.push({
              row_index: idx,
              line_no: row.line_no ?? 0,
              matched_id: top.id,
              matched_name: top.name1 || top.name2 || '',
              candidates,
            });
          }
        } catch { /* skip */ }
        done++;
        setStatusMsg(`自動検索中... ${done}/${visibleRows.length}`);
      }
      setStatusMsg('自動検索完了');

      // 検索結果をバックエンドに保存
      if (uploadId && searchResultsToSave.length > 0) {
        void updateUploadState(uploadId, {
          search_results: searchResultsToSave,
          search_done: true,
        }).catch(() => {/* silent */});
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.rows.length, skipSearch, isMaker]);

  // 倉庫名が変わったら自動保存
  useEffect(() => {
    if (!uploadId || !state.header.warehouse_name) return;
    void updateUploadState(uploadId, {
      confirmed_header: { ...state.header },
    }).catch(() => {/* silent */});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadId, state.header.warehouse_name]);

  function handleSelectUpload(id: string) {
    dispatch({ type: 'RESET' });
    setSearchParams({ uploadId: id });
  }

  async function handleDeleteUpload(id: string) {
    if (!confirm('このアップロードを削除しますか？')) return;
    try {
      await deleteUpload(id);
      setUploads((prev) => (prev ?? []).filter((u) => u.id !== id));
    } catch (e) {
      alert(`削除失敗: ${(e as Error).message}`);
    }
  }

  async function handleOcrRun() {
    if (!uploadId) return;
    dispatch({ type: 'OCR_START' });
    setError(null);
    setStatusMsg('OCR 実行中...（数秒〜数十秒）');
    try {
      const data = await runUploadOcr(uploadId);
      dispatch({
        type: 'OCR_DONE',
        header: data.header,
        sourceId: data.source_id || '',
        items: data.items,
      });
      setStatusMsg(`完了（${data.items.length}行抽出）`);
      setAutoSearchDone(false);
    } catch (e) {
      dispatch({ type: 'OCR_ERROR' });
      setError(e as Error);
      setStatusMsg('');
    }
  }

  async function handleSaveCorrections() {
    const corrections: Correction[] = [];
    // header edits
    for (const key of state.editedFields) {
      if (key.startsWith('header:')) {
        const field = key.slice('header:'.length);
        corrections.push({
          type: 'edit',
          line_no: 0,
          field: `header.${field}`,
          ocr_raw: String(
            (state.headerOriginal as Record<string, unknown>)[field] ?? '',
          ),
          corrected: String((state.header as Record<string, unknown>)[field] ?? ''),
        });
      }
    }
    // row cell edits
    for (const r of state.rows) {
      const orig = state.rowsOriginal.find((o) => o.id === r.id);
      if (r.isDeleted && orig && !r.isAdded) {
        corrections.push({
          type: 'delete',
          line_no: r.line_no ?? 0,
          deleted_row: {
            product_name: orig.product_name,
            size: orig.size,
            quantity: orig.quantity,
            unit: orig.unit,
          },
        });
      } else if (r.isAdded && !r.isDeleted) {
        const afterOrig = state.rowsOriginal.find((o) => o.id === r.afterRowId);
        corrections.push({
          type: 'add',
          after_line_no: afterOrig?.line_no ?? 0,
          added_row: {
            product_name: r.product_name,
            size: r.size,
            quantity: r.quantity,
            unit: r.unit,
          },
        });
      } else if (orig) {
        for (const f of ['product_name', 'size', 'quantity', 'unit'] as const) {
          if (r[f] !== orig[f]) {
            corrections.push({
              type: 'edit',
              line_no: r.line_no ?? 0,
              field: f,
              ocr_raw: String(orig[f] ?? ''),
              corrected: String(r[f] ?? ''),
            });
          }
        }
      }
    }

    // 伝票の区切りだけを変えた場合も一時保存する（修正ログには残さない）
    const hasSlipChanges = state.rows.some(
      (r) => (r.slip ?? 1) !== (state.rowsOriginal.find((o) => o.id === r.id)?.slip ?? 1),
    );
    if (corrections.length === 0 && !hasMatchChanges && !hasOcrNameChanges && !hasSlipChanges) return;
    setStatusMsg('一時保存中...');
    try {
      if (corrections.length > 0) {
        await saveCorrections({
          source_id: state.sourceId || 'unknown',
          source_file: state.fileName || '',
          corrections,
        });
      }
      // 修正済みの OCR 結果 + 検索結果をバックエンドに保存（CORRECTIONS_SAVED の前に実行）
      if (uploadId) {
        const visibleRows = selectors.visibleRows(state);
        const tempSlips = slipInfo(state.rows);
        // 一時保存→復元でデータが欠落しないよう、画面編集値を漏れなく保存する。
        // （row_type が欠けると再読込で備考/小計が明細に戻る不具合になる）
        const updatedItems = visibleRows.map((r) => ({
          line_no: r.line_no ?? 0,
          row_type: r.row_type,
          product_name: r.product_name,
          size: r.size,
          quantity: r.quantity,
          unit: r.unit,
          irisu: r.irisu,
          kosu: r.kosu,
          note: r.note ?? '',
          item_note: r.item_note ?? '',
          item_note2: r.item_note2 ?? '',
          note_name1: r.note_name1,
          note_name2: r.note_name2,
          list_price: r.list_price || undefined,
          estimate_unit_price: r.estimate_unit_price || undefined,
          cost_price: r.cost_price || undefined,
          slip_group: tempSlips.count > 1 ? tempSlips.groupOf[r.id] : undefined,
        }));

        // 品名・寸法が修正された行を再検索
        const editedRowIds = new Set<string>();
        for (const key of state.editedFields) {
          const [rowId, field] = key.split(':');
          if (field === 'product_name' || field === 'size') {
            editedRowIds.add(rowId);
          }
        }
        const updatedMatches = { ...state.matches };
        if (editedRowIds.size > 0) {
          setStatusMsg('修正行を再検索中...');
          for (const rowId of editedRowIds) {
            const row = visibleRows.find((r) => r.id === rowId);
            if (!row) continue;
            const query = `${row.product_name ?? ''} ${row.size ?? ''}`.trim();
            if (!query) continue;
            try {
              const data = await searchProducts({ query, top_k: 10, normalize: true, dept_code: currentDeptCode, warehouse_code: currentWarehouseCode });
              const candidates = data.results ?? [];
              const top = candidates[0];
              if (top) {
                const match = {
                  matchedId: top.id,
                  matchedName: top.name1 || top.name2 || '',
                  candidates,
                };
                dispatch({ type: 'MATCH_SET', rowId, match });
                updatedMatches[rowId] = match;
              }
            } catch { /* skip */ }
          }
        }

        // 1回の API コールで ocr_items + search_results を保存
        const searchResultsToSave = visibleRows
          .map((r, idx) => ({ r, idx }))
          .filter(({ r }) => updatedMatches[r.id])
          .map(({ r, idx }) => {
            const m = updatedMatches[r.id];
            return {
              row_index: idx,
              line_no: r.line_no ?? 0,
              matched_id: m.matchedId,
              matched_name: m.matchedName,
              candidates: m.candidates,
              use_ocr_name: state.ocrNameRows.has(r.id),
            };
          });
        await updateUploadState(uploadId, {
          ocr_header: state.header,
          ocr_items: updatedItems,
          search_results: searchResultsToSave,
          search_done: true,
          header_confirmed: true,
          confirmed_header: state.header,
          confirmed_customer_code: state.customerCode,
          confirmed_site_code: state.siteCode,
        });
      }
      dispatch({ type: 'CORRECTIONS_SAVED' });
      const msgs: string[] = [];
      if (corrections.length > 0) msgs.push(`${corrections.length}件の修正`);
      if (hasMatchChanges) msgs.push('商品変更');
      if (hasOcrNameChanges) msgs.push('商品名切替');
      setStatusMsg(`${msgs.join(' + ')}を保存しました`);
    } catch (e) {
      setError(e as Error);
    }
  }

  // 見積: コード一括登録の対象。チェックした明細があればそれだけ、なければ表示中の明細すべて。
  function bulkCodeTargets() {
    const details = selectors.visibleRows(state).filter(
      (r) => !r.row_type || r.row_type === 'detail',
    );
    const picked = details.filter((r) => selectedRowIds.has(r.id));
    return { targets: picked.length > 0 ? picked : details, picked: picked.length };
  }

  // 見積: 対象の明細に、仮コード(99999950)か汎用コード(名称ﾏﾆｭｱﾙ入力区分='1')を一括登録する。
  // 商品名・寸法はOCR読取値を使う。汎用コードはOCR名採用に切り替えても、選んだコードのまま
  // ERPへ書き込まれる（erpProductCodeOf）。
  function handleBulkCodeRegister(product: SearchResult | null) {
    const { targets } = bulkCodeTargets();
    if (targets.length === 0) return;
    const code = product ? product.id : SPOT_ESTIMATE_CODE;
    const label = product ? `汎用コード ${code}（${product.name1}）` : `仮コード ${code}`;
    if (!confirm(`明細 ${targets.length} 件を${label}で一括登録します（商品名・寸法はOCR読取値）。よろしいですか?`)) return;
    for (const r of targets) {
      dispatch({
        type: 'MATCH_SET',
        rowId: r.id,
        match: {
          matchedId: code,
          matchedName: r.product_name,
          candidates: product
            ? [{ ...product, name_editable: true }]
            : [{ id: code, name1: r.product_name, name2: r.size, category: '' }],
        },
      });
      // 汎用コードは名称をOCR品名に置き換える前提のコードなので、OCR名を採用した状態にする
      if (product && !state.ocrNameRows.has(r.id)) dispatch({ type: 'TOGGLE_OCR_NAME', rowId: r.id });
    }
    setSelectedRowIds(new Set());
    setShowBulkCode(false);
    setStatusMsg(`${targets.length} 件を${label}で一括登録しました`);
  }

  // 見積: 1明細だけ仮コード(99999950)を付与。名称・寸法はOCR読取値を使う。
  function handleRowSpotRegister(rowId: string) {
    const r = selectors.visibleRows(state).find((x) => x.id === rowId);
    if (!r) return;
    dispatch({
      type: 'MATCH_SET',
      rowId: r.id,
      match: {
        matchedId: SPOT_ESTIMATE_CODE,
        matchedName: r.product_name,
        candidates: [{ id: SPOT_ESTIMATE_CODE, name1: r.product_name, name2: r.size, category: '' }],
      },
    });
    setStatusMsg(`1 件を ${SPOT_ESTIMATE_CODE} で登録しました`);
  }

  // 商品コードを直接入力 → マスタ照合して確定（見つからなければ通知のみ）
  async function handleRowCodeInput(rowId: string, code: string) {
    try {
      const { result } = await productLookup(code);
      if (!result) {
        setStatusMsg(`商品コード ${code} はマスタに見つかりませんでした`);
        return;
      }
      dispatch({
        type: 'MATCH_SET',
        rowId,
        match: {
          matchedId: result.id,
          matchedName: result.name1 ?? '',
          candidates: [result],
        },
      });
      setStatusMsg(`商品コード ${code} を設定しました（${result.name1 ?? ''}）`);
    } catch {
      setStatusMsg(`商品コード ${code} の照合に失敗しました`);
    }
  }

  async function handleBatchSearch(useLlm: boolean) {
    const visibleRows = selectors.visibleRows(state);
    const label = useLlm ? 'LLM 検索' : '一括検索';
    setStatusMsg(`${label}中... 0/${visibleRows.length}`);
    let done = 0;
    for (const row of visibleRows) {
      // 備考(区分3)・小計(区分4)は商品がないので検索しない
      if (row.row_type && row.row_type !== 'detail') { done++; continue; }
      const query = `${row.product_name ?? ''} ${row.size ?? ''}`.trim();
      if (!query) {
        done++;
        continue;
      }
      try {
        let candidates;
        if (useLlm) {
          const data = await llmSearchProducts({
            query,
            top_k: 20,
            normalize: true,
            source_id: state.sourceId,
          });
          // LLM の判断に従って candidates を並び替え
          candidates = reorderByLlmResponse(data.candidates ?? [], data.llm_response);
        } else {
          const data = await searchProducts({ query, top_k: 10, normalize: true, dept_code: currentDeptCode, warehouse_code: currentWarehouseCode });
          candidates = data.results ?? [];
        }
        const top = candidates[0];
        if (top) {
          dispatch({
            type: 'MATCH_SET',
            rowId: row.id,
            match: {
              matchedId: top.id,
              matchedName: top.name1 || top.name2 || '',
              candidates,
            },
          });
        }
      } catch {
        // skip individual failures
      }
      done++;
      setStatusMsg(`${label}中... ${done}/${visibleRows.length}`);
    }
    setStatusMsg(`${label}完了`);
  }

  async function handleRowSearch(rowId: string) {
    const row = state.rows.find((r) => r.id === rowId);
    if (!row) return;
    // 備考(区分3)・小計(区分4)は商品がないので検索対象外
    if (row.row_type && row.row_type !== 'detail') return;
    const query = `${row.product_name ?? ''} ${row.size ?? ''}`.trim();
    if (!query) {
      // 空行の場合は MatchPanel を直接開く
      setMatchModalRowId(rowId);
      return;
    }
    setStatusMsg(`検索中...`);
    // 検索中は一旦マッチをクリア（検索フィードバック）
    dispatch({ type: 'MATCH_SET', rowId, match: { matchedId: '', matchedName: '', candidates: [] } });
    try {
      const data = await searchProducts({ query, top_k: 10, normalize: true, dept_code: currentDeptCode, warehouse_code: currentWarehouseCode });
      const candidates = data.results ?? [];
      const top = candidates[0];
      if (top) {
        dispatch({
          type: 'MATCH_SET',
          rowId,
          match: {
            matchedId: top.id,
            matchedName: top.name1 || top.name2 || '',
            candidates,
          },
        });
        setStatusMsg('検索完了');
      } else {
        setStatusMsg('該当なし');
      }
    } catch {
      setStatusMsg('検索失敗');
    }
  }


  async function handleSaveOrder() {
    // 書込み可否の判定は erpBlockReason に一本化する（ERP書込みボタンの活性条件と同一）。
    // ここに別実装のチェックを置くと、片方だけ直したときに「非活性なのに理由が出ない」
    // 「チェック漏れ」が再発する（判定の二重管理）。
    if (erpBlockReason) {
      alert(`ERP書込みできません: ${erpBlockReason}`);
      return;
    }
    const visibleRows = selectors.visibleRows(state);
    // 伝票分割: 行ごとの伝票番号(1..N)。分割していなければ送らない（従来どおりの注文）。
    const slips = slipInfo(state.rows);
    const items = visibleRows.map((r) => {
      const isDetail = !r.row_type || r.row_type === 'detail';
      const isNote = r.row_type === 'note';   // 備考は数量系を持たない(空)。小計/明細は非空
      // 備考(区分3)・小計(区分4)は商品を持たないため照合結果を使わない
      const match = isDetail ? state.matches[r.id] : undefined;
      const useOcr = isDetail && state.ocrNameRows.has(r.id);
      const matchedCandidate = match ? match.candidates.find((c) => c.id === match.matchedId) : undefined;
      const keepMatchedCode = !useOcr || !!matchedCandidate?.name_editable;
      // 小計・中計は商品名1を【小 計】【中 計】（間は全角空白）に固定し、商品名2は空にする
      const subtotalLabel = subtotalLabelOf(r.row_type, r.note_name1 || r.product_name);
      return {
        line_no: r.line_no ?? undefined,
        row_type: r.row_type,
        product_code: erpProductCode(r),
        product_name: r.product_name,
        size: r.size,
        quantity: isNote ? '' : r.quantity,
        unit: r.unit,
        matched_name: useOcr
          ? [r.product_name, r.size].filter(Boolean).join(' ')
          : (match?.matchedName || ''),
        // 明細行: マッチ or OCR名。備考/小計行: 手入力の商品名1/2(未入力は品名/寸法をフォールバック)
        matched_name1: isDetail
          ? (useOcr ? r.product_name : (matchedCandidate?.name1 || match?.matchedName || ''))
          : (subtotalLabel ?? (r.note_name1 || r.product_name)),
        matched_name2: isDetail
          ? (useOcr ? r.size : (matchedCandidate?.name2 || ''))
          : (subtotalLabel !== null ? '' : (r.note_name2 ?? r.size)),
        name_overridden: useOcr,
        irisu: isNote ? '' : (r.irisu || '1'),
        kosu: isNote ? '' : (r.kosu || r.quantity),
        // 仮コードへ倒した行は汎用コード扱いのため分類も付けない。
        // コードを維持する行（汎用コード）はマスタの分類をそのまま残す。
        category: (match && keepMatchedCode) ? (matchedCandidate?.category || '') : '',
        item_note: r.item_note || '',
        item_note2: r.item_note2 || '',
        // 見積の単価3列（定価/見積単価/原価）。空はERP計算に委ねる
        list_price: r.list_price || '',
        estimate_unit_price: r.estimate_unit_price || '',
        cost_price: r.cost_price || '',
        ...(slips.count > 1 ? { slip_group: slips.groupOf[r.id] } : {}),
      };
    });

    // keep ログ送信（修正されなかった product_name）
    const keeps: Correction[] = [];
    for (const r of visibleRows) {
      if (r.isAdded) continue;
      const orig = state.rowsOriginal.find((o) => o.id === r.id);
      if (orig && r.product_name === orig.product_name && r.product_name) {
        keeps.push({
          type: 'keep',
          line_no: r.line_no ?? 0,
          field: 'product_name',
          kept_value: r.product_name,
        });
      }
    }
    if (keeps.length > 0) {
      void saveCorrections({
        source_id: state.sourceId || 'unknown',
        source_file: state.fileName || '',
        corrections: keeps,
      }).catch(() => {/* silent */});
    }

    dispatch({ type: 'SAVE_START' });
    try {
      const result = await saveOrder({
        source_file: state.fileName || '',
        upload_id: uploadId || undefined,
        // erpBlockReason で得意先確定済みを保証しているが、型の絞り込みは効かないため既定値を置く
        customer_code: state.customerCode || '',
        customer_name: state.header.customer_name || '',
        customer_abbr: state.customerAbbr || '',
        site_code: state.siteCode || '',
        site_name: state.siteName || '',
        site_abbr: state.siteAbbr || '',
        staff_code: state.header.staff_code || '',
        input_staff_code: state.header.input_staff_code || '',
        // 出荷担当者(SYUTANCD)。既定は空欄（入力担当者は自動セットしない）。
        shipping_staff_code: state.header.shipping_staff_code || '',
        todoke_code: state.header.todoke_code || '',
        todoke_name: state.header.todoke_code === '088'
          ? (state.header.todoke_name_manual || '')
          : (todokeList.find((t) => t.code === state.header.todoke_code)?.name || ''),
        orderer_name: state.header.orderer_name || '',
        project_name: state.header.project_name || '',
        order_date: state.header.order_date || '',
        time_spec: state.header.time_spec || '',
        contact: state.header.contact || '',
        recipient: state.header.recipient || '',
        note: state.header.note || '',
        // 分割した注文では相手伝票No.を送らない（サーバー側の slip_adnid でも空にする）
        slip_no: slipInfo(state.rows).count > 1 ? '' : (state.header.slip_no || ''),
        warehouse_code: state.header.warehouse_code || '',
        warehouse_name: state.header.warehouse_name || '',
        dept_code: currentDeptCode,
        doc_type: docType,
        items,
      });
      dispatch({ type: 'SAVE_DONE' });
      navigate(`/orders/${result.order_id}`);
    } catch (e) {
      dispatch({ type: 'SAVE_DONE' });
      setError(e as Error);
    }
  }

  // ── uploadId 未指定: アップロード一覧を表示 ──
  if (!uploadId) {
    return (
      <Panel title={`アップロード一覧${uploads ? `（${uploads.length}件）` : ''}`}>
        {uploadsError ? (
          <ErrorBox error={uploadsError} />
        ) : !uploads ? (
          <Spinner />
        ) : uploads.length === 0 ? (
          <p className="text-sm text-muted">
            アップロードされたファイルはありません。「アップロード」タブからファイルを登録してください。
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left whitespace-nowrap">
                  <th className="pb-2 pr-4">登録日時</th>
                  <th className="pb-2 pr-4">種別</th>
                  <th className="pb-2 pr-4">ファイル名</th>
                  <th className="pb-2 pr-4">備考</th>
                  <th className="pb-2 pr-4">入力担当者</th>
                  <th className="pb-2 pr-4">得意先</th>
                  <th className="pb-2 pr-4">現場</th>
                  <th className="pb-2 pr-4">倉庫</th>
                  <th className="pb-2 pr-4">品目数</th>
                  <th className="pb-2 pr-4">ステータス</th>
                  <th className="pb-2">操作</th>
                </tr>
              </thead>
              <tbody>
                {uploads.map((u) => {
                  const filenames = u.files.map((f) => f.filename).join(', ');
                  return (
                  <tr key={u.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 text-xs text-muted whitespace-nowrap">{formatJST(u.created_at)}</td>
                    <td className="py-2 pr-4 text-xs whitespace-nowrap">
                      {u.doc_type === 'estimate_maker' ? (
                        <span className="inline-block px-2 py-0.5 rounded bg-purple-100 text-purple-800">メーカー見積</span>
                      ) : (u.doc_type || '').startsWith('estimate') ? (
                        <span className="inline-block px-2 py-0.5 rounded bg-purple-100 text-purple-800">見積</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded bg-blue-100 text-blue-800">注文</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-xs">
                      <span className="font-mono">{filenames}</span>
                      {u.files.length > 1 && (
                        <span className="ml-1 text-muted">({u.files.length}枚)</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-xs text-muted">{u.note || '-'}</td>
                    <td className="py-2 pr-4 text-xs">
                      {(() => {
                        const raw = u.uploader;
                        if (!raw) return '-';
                        return (/^\d+$/.test(raw) ? staffList.find((s) => s.code === raw)?.name : undefined) || raw;
                      })()}
                    </td>
                    <td className="py-2 pr-4 text-xs">{u.confirmed_header?.customer_name || u.ocr_header?.customer_name || '-'}</td>
                    <td className="py-2 pr-4 text-xs">{u.confirmed_site_name || u.confirmed_site_code || '-'}</td>
                    <td className="py-2 pr-4 text-xs">
                      <WarehouseCell customerCode={u.confirmed_customer_code || ''} warehouseName={u.confirmed_header?.warehouse_name} />
                    </td>
                    <td className="py-2 pr-4 text-xs text-center">{u.ocr_items?.length ?? '-'}</td>
                    <td className="py-2 pr-4">
                      {u.ocr_processing ? (
                        <span className="inline-block px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800 animate-pulse">
                          OCR 処理中...
                        </span>
                      ) : u.ocr_failed ? (
                        <span
                          className="inline-block px-2 py-0.5 rounded text-xs bg-red-100 text-red-800"
                          title={u.ocr_error || 'OCR処理に失敗しました。再実行してください。'}
                        >
                          失敗
                        </span>
                      ) : u.search_done ? (
                        <span className="inline-block px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-800">
                          検索済み
                        </span>
                      ) : u.ocr_done ? (
                        <span className="inline-block px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                          OCR 済み
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">
                          未処理
                        </span>
                      )}
                    </td>
                    <td className="py-2">
                      <div className="flex gap-1">
                        <button
                          className="px-2 py-1 text-xs font-medium rounded bg-primary text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => handleSelectUpload(u.id)}
                          disabled={u.ocr_processing}
                        >
                          {u.ocr_processing ? '処理中...' : u.ocr_done ? 'OCR 確認' : 'OCR 実行'}
                        </button>
                        <button
                          className="px-2 py-1 text-xs font-medium rounded bg-danger text-white hover:bg-red-700"
                          onClick={() => void handleDeleteUpload(u.id)}
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    );
  }

  // ── uploadId 指定: OCR ワークフロー ──
  return (
    <>
      <Panel className="!p-3 !mb-3">
        <div className="flex items-center gap-3">
          {/* 見積を開いても「注文書」と出ていて紛らわしかった（三興バルブ様要望 10/19） */}
          <h2 className="text-lg font-semibold">{isEstimate ? '見積書 OCR' : '注文書 OCR'}</h2>
          {/* 見積は依頼(標準品)とメーカー見積(スポット品)で扱いが異なるため区別して表示する */}
          {isMaker ? (
            <span className="inline-block px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-sm font-medium whitespace-nowrap">メーカー見積</span>
          ) : isEstimate ? (
            <span className="inline-block px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-sm font-medium">見積</span>
          ) : (
            <span className="inline-block px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-sm font-medium">注文</span>
          )}
          <p className="text-sm">{state.fileName || 'ファイル読み込み中...'}</p>
          {/* 一覧の「品目数」と同じく全行（削除した行を除く）を数え、明細(区分0)の数も並べる */}
          {state.rows.length > 0 && (() => {
            const live = selectors.visibleRows(state);
            const details = live.filter((r) => (r.row_type ?? 'detail') === 'detail').length;
            return (
              <span className="text-sm text-muted whitespace-nowrap">
                品目数 <strong className="text-slate-800">{live.length}</strong>
                <span className="mx-1">/</span>
                明細数 <strong className="text-slate-800">{details}</strong>
              </span>
            );
          })()}
          <Button onClick={handleOcrRun} disabled={state.phase === 'ocr_running' || ocrProcessing || !state.previewUrl}>
            {ocrProcessing ? 'バックグラウンド OCR 処理中...' : state.phase === 'ocr_running' ? 'OCR 実行中...' : 'OCR 実行'}
          </Button>
          <Button
            variant="ghost"
            onClick={async () => {
              if (uploadId && state.customerCode) {
                await updateUploadState(uploadId, {
                  header_confirmed: true,
                  confirmed_header: state.header,
                  confirmed_customer_code: state.customerCode,
                  confirmed_site_code: state.siteCode,
                  confirmed_site_name: state.siteName || '',
                }).catch(() => {/* silent */});
              }
              dispatch({ type: 'RESET' });
              setSearchParams({});
            }}
          >
            一覧に戻る
          </Button>
          {statusMsg && <span className="text-sm text-muted">{statusMsg}</span>}
        </div>
      </Panel>

      <div className="flex" style={{ height: 'calc(100vh - 150px)' }}>
        {/* 左: プレビュー */}
        {state.previewUrl && state.fileName && (
          <>
            <div style={{ width: `${splitPercent}%` }} className="shrink-0 h-full">
              <Panel className="h-full !mb-0 !p-3 flex flex-col">
                {previewFileCount > 1 && uploadId && (
                  <div className="flex items-center gap-2 mb-2">
                    {Array.from({ length: previewFileCount }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setPreviewFileIndex(i)}
                        className={`px-3 py-1 text-sm rounded ${
                          i === previewFileIndex
                            ? 'bg-primary text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {i + 1}枚目
                      </button>
                    ))}
                  </div>
                )}
                <PreviewPanel
                  url={uploadId ? uploadFileUrl(uploadId, previewFileIndex) : state.previewUrl}
                  fileName={state.fileName}
                  uploadId={uploadId ?? undefined}
                  fileIndex={previewFileIndex}
                />
              </Panel>
            </div>
            {/* ドラッグハンドル */}
            <div
              className="w-2 cursor-col-resize hover:bg-primary/20 active:bg-primary/30 shrink-0 rounded select-none"
              onMouseDown={(e) => {
                e.preventDefault();
                setIsDragging(true);
                const startX = e.clientX;
                const startPercent = splitPercent;
                const container = (e.target as HTMLElement).parentElement!;
                const containerWidth = container.getBoundingClientRect().width;
                const onMove = (ev: MouseEvent) => {
                  ev.preventDefault();
                  const delta = ev.clientX - startX;
                  const newPercent = startPercent + (delta / containerWidth) * 100;
                  setSplitPercent(Math.min(80, Math.max(20, newPercent)));
                };
                const onUp = () => {
                  setIsDragging(false);
                  document.removeEventListener('mousemove', onMove);
                  document.removeEventListener('mouseup', onUp);
                };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
              }}
            />
            {/* ドラッグ中に iframe がマウスイベントを奪うのを防ぐオーバーレイ */}
            {isDragging && <div className="fixed inset-0 z-50 cursor-col-resize" />}
          </>
        )}

        {/* 右: OCR 結果 */}
        <div className={`${state.previewUrl ? 'flex-1 min-w-0' : 'w-full'} overflow-auto h-full`}>
          {state.phase !== 'idle' && state.rows.length > 0 && (
            <Panel title="OCR 抽出結果" className="h-full !mb-0 flex flex-col">
              {error && <ErrorBox error={error} />}

              <HeaderEditor
                header={state.header}
                customerCode={state.customerCode}
                siteCode={state.siteCode}
                siteName={state.siteName || state.ocrSiteName}
                isEstimate={isEstimate}
                slipSplit={slipInfo(state.rows).count > 1}
                isEdited={(field) => selectors.isHeaderEdited(state, field)}
                onChange={(field, value) => dispatch({ type: 'HEADER_EDIT', field, value })}
                onCustomerSelect={(code, name, staffCode, nameEditable, abbr) => {
                  dispatch({ type: 'CUSTOMER_SET', code, name, abbr, staffCode, nameEditable });
                  if (uploadId) {
                    void updateUploadState(uploadId, {
                      header_confirmed: true,
                      confirmed_customer_code: code,
                      confirmed_site_code: null,
                      confirmed_site_name: null,
                    }).catch(() => {/* silent */});
                  }
                }}
                onSiteSelect={(code, name, nameEditable, abbr) => {
                  dispatch({ type: 'SITE_SET', code, name, abbr, nameEditable });
                  if (uploadId) {
                    void updateUploadState(uploadId, {
                      confirmed_site_code: code,
                      confirmed_site_name: name || '',
                    }).catch(() => {/* silent */});
                  }
                }}
                customerNameEditable={state.customerNameEditable}
                siteNameEditable={state.siteNameEditable}
                onSiteNameChange={(name) => {
                  dispatch({ type: 'SITE_SET', code: state.siteCode!, name, nameEditable: true });
                }}
                suppressAutoSearch={!!state.customerCode}
                suppressSiteAutoMatch={state.headerConfirmed}
              />

              <RowTable
                rows={state.rows}
                matches={state.matches}
                matchesOriginal={state.matchesOriginal}
                confirmedRows={state.confirmedRows}
                isCellEdited={(rowId, field) => selectors.isCellEdited(state, rowId, field)}
                onCellEdit={(rowId, field, value) =>
                  dispatch({ type: 'CELL_EDIT', rowId, field: field as keyof Row, value })
                }
                onRowDelete={(rowId) => dispatch({ type: 'ROW_DELETE', rowId })}
                onRowRemove={(rowId) => dispatch({ type: 'ROW_REMOVE', rowId })}
                onRowRestore={(rowId) => dispatch({ type: 'ROW_RESTORE', rowId })}
                onRowAdd={(afterRowId) => dispatch({ type: 'ROW_ADD', afterRowId })}
                onRowTypeChange={(rowId, value) => dispatch({ type: 'ROW_TYPE_SET', rowId, value })}
                onRowSearch={handleRowSearch}
                onRowSpotCode={handleRowSpotRegister}
                erpProductCode={erpProductCode}
                onMoveRow={(rowId, direction) => dispatch({ type: 'MOVE_ROW', rowId, direction })}
                onMoveRowTo={(rowId, targetId) => dispatch({ type: 'MOVE_ROW_TO', rowId, targetId })}
                onSlipSplit={SLIP_SPLIT_ENABLED ? (rowId) => dispatch({ type: 'SLIP_SPLIT', rowId }) : undefined}
                onSlipMerge={SLIP_SPLIT_ENABLED ? (rowId) => dispatch({ type: 'SLIP_MERGE', rowId }) : undefined}
                onRowCodeInput={handleRowCodeInput}
                onMatchOpen={(rowId) => setMatchModalRowId(rowId)}
                onToggleOcrName={(rowId) => dispatch({ type: 'TOGGLE_OCR_NAME', rowId })}
                ocrNameRows={state.ocrNameRows}
                onSort={(key) => dispatch({ type: 'SORT_ROWS', key })}
                sortKey={state.sortKey}
                sortAsc={state.sortAsc}
                isEstimate={isEstimate}
                isReturn={state.header.todoke_code === '011'}
                selectedRowIds={isEstimate ? selectedRowIds : undefined}
                onToggleSelect={isEstimate ? (ids, on) => setSelectedRowIds((prev) => {
                  const next = new Set(prev);
                  ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
                  return next;
                }) : undefined}
              />

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button
                  onClick={() => handleBatchSearch(false)}
                  variant="secondary"
                >
                  一括検索
                </Button>
                {import.meta.env.DEV && (
                  <Button
                    onClick={() => handleBatchSearch(true)}
                    variant="secondary"
                  >
                    LLM 検索
                  </Button>
                )}
                {isEstimate && (
                  <Button onClick={() => setShowBulkCode(true)} variant="secondary">
                    コード一括登録{bulkCodeTargets().picked > 0 ? `（${bulkCodeTargets().picked}件）` : ''}
                  </Button>
                )}
                <Button onClick={handleSaveCorrections} disabled={!hasUnsaved && !hasMatchChanges && !hasOcrNameChanges} variant="secondary">
                  一時保存
                </Button>
                <Button
                  onClick={() => setShowErpConfirm(true)}
                  disabled={!!erpBlockReason || state.phase === 'saving'}
                  variant="primary"
                >
                  ERP書込み
                </Button>
                {erpBlockReason && (
                  <span className="text-sm text-amber-600">ERP書込み不可: {erpBlockReason}</span>
                )}
                {statusMsg && <span className="text-sm text-success">{statusMsg}</span>}
              </div>
            </Panel>
          )}
        </div>
      </div>

      {showBulkCode && (
        <BulkCodeModal
          targetCount={bulkCodeTargets().targets.length}
          hasSelection={bulkCodeTargets().picked > 0}
          spotCode={SPOT_ESTIMATE_CODE}
          onClose={() => setShowBulkCode(false)}
          onSelectSpot={() => handleBulkCodeRegister(null)}
          onSelectCode={(p) => handleBulkCodeRegister(p)}
        />
      )}

      {matchModalRowId && (
        <MatchPanel
          open={true}
          candidates={state.matches[matchModalRowId]?.candidates ?? recentProducts}
          query={(() => {
            const r = state.rows.find((x) => x.id === matchModalRowId);
            return r ? `${r.product_name ?? ''} ${r.size ?? ''}`.trim() : '';
          })()}
          onClose={() => setMatchModalRowId(null)}
          onSelect={(result, _rank) => {
            const existing = state.matches[matchModalRowId]?.candidates ?? [];
            const candidates = existing.some((c) => c.id === result.id)
              ? existing
              : [result, ...existing];
            dispatch({
              type: 'MATCH_SET',
              rowId: matchModalRowId,
              match: {
                matchedId: result.id,
                matchedName: result.name1 || result.name2 || '',
                candidates,
              },
            });
            // よく使う商品に追加（重複排除、最新を先頭に）
            setRecentProducts((prev) => {
              const filtered = prev.filter((p) => p.id !== result.id);
              return [{ id: result.id, name1: result.name1 || '', name2: result.name2, category: result.category }, ...filtered].slice(0, 20);
            });
            setMatchModalRowId(null);
          }}
        />
      )}

      {/* ERP書込み確認モーダル */}
      {showErpConfirm && (
        <Modal open={showErpConfirm} onClose={() => setShowErpConfirm(false)} title="ERP書込み確認" widthClass="max-w-5xl">
          {(() => {
            // 伝票分割している場合は、書き込む前に分け方をもう一度見せる
            const sl = slipInfo(state.rows);
            return sl.count > 1 ? (
              <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <strong>{sl.count}伝票に分けて書き込みます：</strong>
                {sl.sizes.map((n, i) => `伝票${i + 1}/${sl.count} ${n}件`).join('、')}
                （合計 {sl.sizes.reduce((a, b) => a + b, 0)}件）
                <div className="mt-1">伝票を分割しているため、相手伝票No.はERPへ送りません。</div>
              </div>
            ) : null;
          })()}
          <div className="bg-slate-50 p-3 rounded mb-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <div><strong>得意先:</strong> {state.header.customer_name} ({state.customerCode}){state.customerAbbr ? ` 略称: ${state.customerAbbr}` : ''}</div>
            <div><strong>現場:</strong> {state.siteCode ? `${state.siteName} (${state.siteCode})` : '-'}{state.siteAbbr ? ` 略称: ${state.siteAbbr}` : ''}</div>
            <div><strong>注文者:</strong> {state.header.orderer_name || '-'}</div>
            <div><strong>日付:</strong> {state.header.order_date || '-'}</div>
            <div><strong>入力担当者:</strong> {staffList.find((s) => s.code === state.header.input_staff_code)?.name || state.header.input_staff_code || '-'}</div>
            <div><strong>時間指定:</strong> {state.header.time_spec || '-'}</div>
            <div><strong>連絡先:</strong> {state.header.contact || '-'}</div>
            <div><strong>受取人:</strong> {state.header.recipient || '-'}</div>
            <div><strong>備考:</strong> {state.header.note || '-'}</div>
            <div><strong>届け先:</strong> {state.header.todoke_code ? `${state.header.todoke_code === '088' ? state.header.todoke_name_manual : todokeList.find((t) => t.code === state.header.todoke_code)?.name || ''} (${state.header.todoke_code})` : '-'}</div>
            <div><strong>相手伝票No.:</strong> {slipInfo(state.rows).count > 1 ? '-（分割のため送りません）' : (state.header.slip_no || '-')}</div>
            <div><strong>倉庫:</strong> {state.header.warehouse_name || '-'}</div>
          </div>

          <div className="overflow-auto max-h-[50vh]">
            <table className="text-sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>品名（注文書）</th>
                  <th>寸法</th>
                  <th>単位</th>
                  <th className="text-right">数量</th>
                  <th className="text-right">入数</th>
                  <th className="text-right">個数</th>
                  <th>商品名1</th>
                  <th>商品名2</th>
                  <th>分類</th>
                  <th>商品コード</th>
                </tr>
              </thead>
              <tbody>
                {selectors.visibleRows(state).map((r, i) => {
                  const match = state.matches[r.id];
                  const useOcr = state.ocrNameRows.has(r.id);
                  const candidate = match?.candidates.find((c) => c.id === match.matchedId);
                  return (
                    <tr key={r.id} className={i % 2 === 1 ? 'bg-slate-50' : ''}>
                      <td>{i + 1}</td>
                      <td>{r.product_name}</td>
                      <td>{r.size}</td>
                      <td>{r.unit}</td>
                      <td className="text-right">{r.quantity}</td>
                      <td className="text-right">{r.irisu || '1'}</td>
                      <td className="text-right">{r.kosu || r.quantity}</td>
                      <td>{useOcr ? [r.product_name, r.size].filter(Boolean).join(' ') : (candidate?.name1 || match?.matchedName || '')}</td>
                      <td className="text-xs text-muted">{useOcr ? '' : (candidate?.name2 || '')}</td>
                      <td className="text-xs text-muted">{useOcr ? '' : (candidate?.category || '')}</td>
                      <td className="text-xs font-mono">{erpProductCode(r)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowErpConfirm(false)}>キャンセル</Button>
            <Button
              variant="primary"
              disabled={state.phase === 'saving'}
              onClick={() => {
                setShowErpConfirm(false);
                void handleSaveOrder();
              }}
            >
              {state.phase === 'saving' ? '書込み中...' : '書込み実行'}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}

/**
 * LLM のレスポンステキストから top3 ID を抽出し、candidates を並び替える。
 */
function reorderByLlmResponse<T extends { id: string }>(
  candidates: T[],
  llmResponse: string | undefined,
): T[] {
  if (!llmResponse) return candidates;
  try {
    let text = llmResponse.trim();
    if (text.startsWith('```')) {
      text = text.replace(/^```(?:json)?\s*/, '').replace(/```\s*$/, '');
    }
    const parsed = JSON.parse(text) as { top3?: Array<{ id: string }> };
    const topIds = (parsed.top3 ?? []).map((t) => String(t.id));
    if (topIds.length === 0) return candidates;

    const byId = new Map(candidates.map((c) => [String(c.id), c] as const));
    const topPicks: T[] = [];
    for (const id of topIds) {
      const found = byId.get(id);
      if (found) {
        topPicks.push(found);
        byId.delete(id);
      }
    }
    return [...topPicks, ...byId.values()];
  } catch {
    return candidates;
  }
}
